import styled from 'styled-components';
import { Spacing } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';

/**
 * Per-question dash progress for the PHQ-9 / GAD-7 screener (one dash per item),
 * matching Boris's prototype `rg-progress` / `rg-dash` 1:1: thin gold pills,
 * with done and current filled (current at 55% opacity) and upcoming a lighter
 * gold track.
 *
 * Distinct from the app's whole-flow segmented `ProgressBar` (hidden on the
 * screener). Dashes are decorative (`aria-hidden`); the "Question X of Y" text
 * conveys progress to assistive technology.
 */
type QuestionProgressProps = {
  total: number;
  /** 0-based index of the active question. */
  current: number;
};

type DashState = 'done' | 'current' | 'upcoming';

const DashRow = styled.div`
  display: flex;
  width: 100%;
  gap: 5px;
  margin-bottom: ${Spacing.x2}px;
`;

const Dash = styled.span<{ $state: DashState }>`
  flex: 1;
  height: 4px;
  border-radius: 999px;
  background-color: ${({ $state }) =>
    $state === 'upcoming' ? BorisScreener.dashTrack : BorisScreener.dashFill};
  opacity: ${({ $state }) => ($state === 'current' ? 0.55 : 1)};
  transition: background-color 0.3s ease, opacity 0.3s ease;
`;

const QuestionProgress = ({ total, current }: QuestionProgressProps) => (
  <DashRow aria-hidden="true">
    {Array.from({ length: total }, (_, index) => {
      const state: DashState = index < current ? 'done' : index === current ? 'current' : 'upcoming';
      return <Dash key={index} $state={state} data-state={state} />;
    })}
  </DashRow>
);

export { QuestionProgress };
